<template>
  <video ref="player" class="w-full h-full video-js vjs-big-play-centered overflow-hidden"></video>
</template>

<script>
import videojs from 'video.js';
import 'video.js/dist/video-js.min.css';
import 'videojs-youtube/dist/Youtube.min.js';
import { FirebaseAnalytics } from '@/utils/firebase-analytics-wrapper';
import { trackEvent } from '@/utils/mixpanel';
import { getConferenceType } from '@/utils/conference-type';

// Get the Component base class from Video.js
const Component = videojs.getComponent('Component');

// ES6 class extending Component (videojs.extend was removed in v8)
class MyButton extends Component {
  constructor(player, options) {
    super(player, options);

    if (options.text) {
      this.updateTextContent(options.text);
    }
  }

  createEl() {
    return videojs.dom.createEl('div', {
      className: 'vjs-button',
    });
  }

  updateTextContent(text) {
    if (typeof text !== 'string') {
      text = 'Title Unknown';
    }
    videojs.dom.emptyEl(this.el());
    videojs.dom.appendContent(this.el(), text);
  }
}

// Register the component with Video.js, so it can be used in players.
videojs.registerComponent('MyButton', MyButton);

export default {
  name: 'VideoPlayer',
  props: {
    options: {
      type: Object,
      default() {
        return {};
      },
    },
    videoTitle: {
      type: String,
      default: '',
    },
    videoId: {
      type: String,
      default: '',
    },
  },
  data() {
    return {
      player: null,
      tenFlag: false,
      thrityFlag: false,
      fiftyFlag: false,
      seventyFlag: false,
      ninetyFlag: false,
      lastTimeUpdate: 0,
      accumulatedTimeWatched: 0,
      sendInterval: 60, // in seconds
    };
  },
  mounted() {
    this.player = videojs(this.$refs.player, this.options);
    this.player.removeChild('');
    this.setupIosFullscreen();
    // Reset baseline on seek so timeupdate delta starts from the destination, not the origin.
    // https://developer.mozilla.org/en-US/docs/Web/API/HTMLMediaElement/seeking_event
    this.player.on('seeking', () => {
      this.lastTimeUpdate = this.player.currentTime();
    });
    this.player.on('timeupdate', this.trackWatchDuration);
    this.player.on('ended', this.sendAccumulatedTimeWatched);
    // flush watch time on pause; guard skips the auto-pause fired just before 'ended'
    this.player.on('pause', () => {
      if (this.accumulatedTimeWatched >= 1) {
        this.sendAccumulatedTimeWatched();
      }
    });
    this.player.on('play', () => {
      const videoTitle = this.options.sources[0].src;
      FirebaseAnalytics.logEvent({
        name: 'video_play',
        params: {
          video_title: videoTitle,
          video_status: 'start',
          video_provider: 'html',
        },
      });
      trackEvent('Video Played', {
        video_title: videoTitle,
        video_status: 'start',
        video_provider: 'html',
      });
    });
    this.player.on('ended', () => {
      const videoTitle = this.options.sources[0].src;
      FirebaseAnalytics.logEvent({
        name: 'video_play',
        params: {
          video_title: videoTitle,
          video_status: 'completed',
          video_provider: 'html',
        },
      });
      trackEvent('Video Played', {
        video_title: videoTitle,
        video_status: 'completed',
        video_provider: 'html',
      });
    });
    this.player.on('progress', () => {
      const percentage = Math.round((this.player.currentTime() / this.player.duration()) * 100);

      if (percentage >= 90 && !this.ninetyFlag) {
        const videoTitle = this.options.sources[0].src;
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '90',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '90',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.ninetyFlag = true;
      } else if (percentage >= 70 && !this.seventyFlag) {
        const videoTitle = this.options.sources[0].src;
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '70',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '70',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.seventyFlag = true;
      } else if (percentage >= 50 && !this.fiftyFlag) {
        const videoTitle = this.options.sources[0].src;
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '50',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '50',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.fiftyFlag = true;
      } else if (percentage >= 30 && !this.thirtyFlag) {
        const videoTitle = this.options.sources[0].src;
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '30',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '30',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.thirtyFlag = true;
      } else if (percentage >= 10 && !this.tenFlag) {
        const videoTitle = this.options.sources[0].src;
        FirebaseAnalytics.logEvent({
          name: 'video_play',
          params: {
            video_title: videoTitle,
            video_percent: '10',
            video_status: 'progress',
            video_provider: 'html',
          },
        });
        trackEvent('Video Played', {
          video_title: videoTitle,
          video_percent: '10',
          video_status: 'progress',
          video_provider: 'html',
        });
        this.tenFlag = true;
      }
    });
  },
  beforeUnmount() {
    if (this.player) {
      this.sendAccumulatedTimeWatched();
      if (this._safeAreaUnlockTimeout) clearTimeout(this._safeAreaUnlockTimeout);
      const videoEl = this.player.tech()?.el();
      if (videoEl) {
        videoEl.removeEventListener('webkitbeginfullscreen', this.onEnterFullscreen);
        videoEl.removeEventListener('webkitendfullscreen', this.onExitFullscreen);
      }
      this.player.dispose();
    }
  },
  methods: {
    trackWatchDuration() {
      const currentTime = this.player.currentTime();

      // Accumulate time actually watched (mirrors Video/VideoPlayer.vue).
      const delta = currentTime - this.lastTimeUpdate;
      if (delta > 0) {
        this.accumulatedTimeWatched += delta;
      }

      // Flush to analytics every 60 seconds of watch time
      if (this.accumulatedTimeWatched >= this.sendInterval) {
        this.sendAccumulatedTimeWatched();
      }

      this.lastTimeUpdate = currentTime;
    },
    sendAccumulatedTimeWatched() {
      try {
        trackEvent('Video Duration', {
          video_id: this.videoId || undefined,
          video_title: this.videoTitle || this.options.sources?.[0]?.src,
          video_length: Number.isFinite(this.player.duration())
            ? this.player.duration()
            : undefined,
          time_watched: this.accumulatedTimeWatched,
          page_url: this.$route?.path,
          conference: getConferenceType(),
        });
      } catch {
        // noop
      }

      this.accumulatedTimeWatched = 0;
    },
    // iOS-only fixes for Video.js inside Capacitor 8.
    setupIosFullscreen() {
      if (!this.isIosDevice()) return;
      this.$nextTick(() => {
        // 1. Capacitor 8 reports document.fullscreenEnabled=true, so Video.js uses the element
        //    Fullscreen API. On iOS that fills the viewport ignoring the home bar and, on exit,
        //    clobbers the SafeArea plugin's CSS variables (breaking the layout until app restart).
        //    Blank out the element Fullscreen API on this player (clone fsApi_ first — it's a
        //    shared module object) so Video.js falls back to its coordinated native iOS path
        //    (tech.enterFullScreen), which keeps player state in sync and respects the home bar.
        if (this.player.fsApi_) {
          this.player.fsApi_ = {
            ...this.player.fsApi_,
            requestFullscreen: null,
            exitFullscreen: null,
          };
        }

        // 2. On iOS the <video> composites on its own GPU layer and, inside a transformed ancestor
        //    (e.g. the experience modal), WebKit paints it OVER the DOM control bar — the buttons
        //    are tappable but never drawn. This class promotes the control bar to its own layer
        //    above the video (see .vjs-ios-inline-controls in _external.scss).
        this.player.addClass('vjs-ios-inline-controls');
        // Give the controls a slightly longer 4s idle window before they auto-hide.
        this.player.options_.inactivityTimeout = 4000;

        // 3. Save/restore the safe-area insets around native fullscreen so the layout survives.
        const videoEl = this.player.tech()?.el();
        if (videoEl) {
          videoEl.addEventListener('webkitbeginfullscreen', this.onEnterFullscreen);
          videoEl.addEventListener('webkitendfullscreen', this.onExitFullscreen);
        }
      });
    },
    isIosDevice() {
      const isIOSDevice = /iphone|ipad|ipod/i.test(navigator.userAgent);
      const isIPadOS =
        /macintosh/i.test(navigator.userAgent) &&
        'ontouchend' in document &&
        navigator.maxTouchPoints > 0;
      return isIOSDevice || isIPadOS;
    },
    onEnterFullscreen() {
      const root = document.documentElement;
      const computed = getComputedStyle(root);
      this._savedSafeArea = {
        top: computed.getPropertyValue('--ion-safe-area-top').trim(),
        bottom: computed.getPropertyValue('--ion-safe-area-bottom').trim(),
        left: computed.getPropertyValue('--ion-safe-area-left').trim(),
        right: computed.getPropertyValue('--ion-safe-area-right').trim(),
      };
    },
    onExitFullscreen() {
      const root = document.documentElement;
      if (this._savedSafeArea) {
        root.style.setProperty('--safe-area-inset-top', this._savedSafeArea.top);
        root.style.setProperty('--safe-area-inset-bottom', this._savedSafeArea.bottom);
        root.style.setProperty('--safe-area-inset-left', this._savedSafeArea.left);
        root.style.setProperty('--safe-area-inset-right', this._savedSafeArea.right);
      }
      clearTimeout(this._safeAreaUnlockTimeout);
      this._safeAreaUnlockTimeout = setTimeout(() => {
        root.style.removeProperty('--safe-area-inset-top');
        root.style.removeProperty('--safe-area-inset-bottom');
        root.style.removeProperty('--safe-area-inset-left');
        root.style.removeProperty('--safe-area-inset-right');
      }, 500);
    },
    onReadyPlay() {},
    onPlay() {
      this.player.poster(this.options.poster[0].src);
      this.player.src(this.options.sources);
      // ios plays videos on full-screen automatically, so not requesting fullscreen
      // solves the issue with regards to videos sourced from youtube not restoring
      // properly from full screen
      if (!this.isIos()) {
        this.player.requestFullscreen();
      }
      setTimeout(() => {
        this.player.play();
      }, 100);
    },
    isIos() {
      return [
        'iPad Simulator',
        'iPhone Simulator',
        'iPod Simulator',
        'iPad',
        'iPhone',
        'iPod',
      ].includes(navigator.platform);
      // iPad on iOS 13 detection
      // (navigator.userAgent.includes('Mac') && 'ontouchend' in document)
    },
    onPause() {
      this.player.pause();
    },
  },
};
</script>
